import React from 'react';
import './custom.css';
import AboutVisual from './components/AboutVisual';

const pillars = [
  {
    title: 'Inspired',
    lead: 'We think bigger.',
    body:
      'Bold vision, clear strategy, and creative ambition that turns imagination into momentum.',
  },
  {
    title: 'Invested',
    lead: 'We build bolder.',
    body:
      'Ideas backed by models built for revenue, resilience, and long-term growth.',
  },
  {
    title: 'Impactful',
    lead: 'We deliver better.',
    body:
      'Partnerships designed for measurable outcomes and lasting community value.',
  },
];

const services = [
  {
    title: 'Innovation Strategy',
    body: 'Clarify vision, validate market potential, and define a roadmap that turns imagination into momentum.',
  },
  {
    title: 'Profit-Driven Growth',
    body: 'Build sustainable business models and execution plans that drive revenue, efficiency, and competitiveness.',
  },
  {
    title: 'Positive Impact',
    body: 'Design initiatives that elevate communities, improve outcomes, and leave a lasting positive footprint.',
  },
  {
    title: 'Brand Partnerships',
    body: 'Collaborate with organizations that share our focus on impact, innovation, and inclusive growth.',
  },
  {
    title: 'Venture Launch',
    body: 'From concept strategy to launch support, we align creativity, performance, and social responsibility.',
  },
  {
    title: 'Community Strategy',
    body: 'Build solutions that reflect the people and communities they serve with measurable social outcomes.',
  },
];

const executives = [
  { name: 'Brien Bell', title: 'CEO' },
  { name: 'LaChandra Bell', title: 'COO' },
  { name: 'Kortnee Bell', title: 'VP Operations' },
  { name: 'Khaylan Bell', title: 'VP Marketing' },
];

export default function App() {
  return (
    <div className="site-wrapper">
      <nav className="site-nav">
        <div className="container nav-inner">
          <a className="nav-brand" href="#top">
            NVESTED REALITY
          </a>
          <ul className="nav-links">
            <li><a href="#about">About</a></li>
            <li><a href="#services">Services</a></li>
            <li><a href="#partners">Partners</a></li>
            <li><a href="#team">Team</a></li>
            <li><a href="#contact">Contact</a></li>
          </ul>
        </div>
      </nav>

      <header className="hero-section" id="top">
        <div className="container hero-content">
          <div className="hero-grid">
            <div className="hero-brand">
              <h1 className="brand-hero-title mb-0">
                <span className="visually-hidden">NVESTED REALITY</span>
                <img
                  src={`${process.env.PUBLIC_URL}/nvested-logo.png`}
                  alt=""
                  className="brand-logo-img"
                  width={220}
                  height={220}
                />
                <span className="brand-subtitle" aria-hidden="true">
                  From Idea to Reality
                </span>
              </h1>
            </div>
            <div className="hero-statement">
              <h2 className="hero-headline">Dare to build</h2>
              <p className="hero-tagline">
                We bring imagination to life, make ideas profitable, and impact
                society in a positive manner.
              </p>
            </div>
          </div>
        </div>
      </header>

      <main>
        <section className="pillars-section">
          <div className="container">
            <div className="pillars-grid">
              {pillars.map((pillar) => (
                <article className="pillar-card" key={pillar.title}>
                  <h3 className="pillar-title">{pillar.title}</h3>
                  <p className="pillar-lead">{pillar.lead}</p>
                  <p className="pillar-body">{pillar.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section-block about-section" id="about">
          <div className="container">
            <p className="section-label">About</p>
            <h2 className="display-heading">
              We make it possible to stand out in this crowded world.
            </h2>
            <div className="about-copy">
              <p>
                NVESTED REALITY LLC partners with innovators, founders, and
                mission-driven organizations to transform bold ideas into
                real-world ventures with cultural relevance and broad community
                impact.
              </p>
              <AboutVisual />
            </div>
          </div>
        </section>

        <section className="section-block section-alt" id="services">
          <div className="container">
            <p className="section-label">Services</p>
            <h2 className="display-heading display-heading-sm mb-5">
              What we deliver
            </h2>
            <div className="services-grid">
              {services.map((service) => (
                <article className="service-card" key={service.title}>
                  <h3 className="service-title">{service.title}</h3>
                  <p className="service-body">{service.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section-block" id="partners">
          <div className="container">
            <p className="section-label">Clients</p>
            <h2 className="display-heading display-heading-sm mb-2">
              Our partners
            </h2>
            <p className="lead-text mb-5">
              We collaborate with organizations that share our focus on impact,
              innovation, and inclusive growth.
            </p>
            <ul className="partner-logos">
              <li>
                <a
                  className="partner-logo-slot partner-logo-link"
                  href="https://bbellweb.info"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="partner-logo-circle">
                    <img
                      src={`${process.env.PUBLIC_URL}/partners/bbellweb-logo.png`}
                      alt="BBELLWEB"
                      className="partner-logo-img"
                    />
                  </span>
                </a>
              </li>
              <li>
                <a
                  className="partner-logo-slot partner-logo-link"
                  href="https://libbythelion.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="partner-logo-circle">
                    <img
                      src={`${process.env.PUBLIC_URL}/partners/libby-logo.png`}
                      alt="Libby the Lion & Friends"
                      className="partner-logo-img"
                    />
                  </span>
                </a>
              </li>
              <li>
                <div className="partner-logo-slot partner-logo-placeholder">
                  <span className="partner-logo-circle partner-logo-circle--placeholder">
                    Your logo here
                  </span>
                </div>
              </li>
            </ul>
          </div>
        </section>

        <section className="section-block section-alt">
          <div className="container">
            <p className="section-label">Commitment</p>
            <h2 className="display-heading display-heading-sm mb-5">
              Our diversity commitment
            </h2>
            <div className="pillars-grid">
              <article className="pillar-card">
                <h3 className="pillar-title">Representation</h3>
                <p className="pillar-body">
                  We intentionally build and support solutions that reflect the
                  people and communities they serve.
                </p>
              </article>
              <article className="pillar-card">
                <h3 className="pillar-title">Equity</h3>
                <p className="pillar-body">
                  We help founders and teams access strategic pathways to funding,
                  growth, and long-term success.
                </p>
              </article>
              <article className="pillar-card">
                <h3 className="pillar-title">Community</h3>
                <p className="pillar-body">
                  Every engagement is designed to produce measurable results and
                  positive social outcomes.
                </p>
              </article>
            </div>
          </div>
        </section>

        <section className="section-block" id="team">
          <div className="container">
            <p className="section-label">Leadership</p>
            <h2 className="display-heading display-heading-sm mb-2">
              Executive team
            </h2>
            <p className="lead-text mb-5">
              Experienced leaders guiding strategy, operations, and creative
              execution across every engagement.
            </p>
            <ul className="row g-4 list-unstyled mb-0 executive-grid">
              {executives.map((exec) => (
                <li className="col-md-6 col-lg-3" key={exec.name}>
                  <div className="executive-card">
                    <h3 className="executive-name h5 mb-1">{exec.name}</h3>
                    <p className="executive-title mb-0">{exec.title}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </main>

      <footer className="footer-section" id="contact">
        <div className="container footer-inner">
          <p className="section-label">Contact</p>
          <h2 className="display-heading display-heading-sm mb-3">
            Start a project with us
          </h2>
          <p className="footer-copy mb-4">
            Partner with NVESTED REALITY LLC to turn your vision into profitable,
            purpose-driven reality.
          </p>
          <a className="btn btn-brand" href="mailto:contact@nvestedreality.com">
            contact@nvestedreality.com
          </a>
          <p className="footer-legal mt-5 mb-0">
            &copy; {new Date().getFullYear()} NVESTED REALITY LLC. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
